import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError, money } from "@/lib/api";
import { Loader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Database } from "lucide-react";

const CFG = {
  retailers: { title: "Retailers", url: "/admin/retailers", approve: true, cols: [
    ["shop_name", "Shop"], ["owner_name", "Owner"], ["business_type", "Type"], ["city", "City"], ["state", "State"],
    ["verification_status", "Status", "badge"]] },
  products: { title: "Products", url: "/admin/products", cols: [
    ["name", "Product"], ["manufacturer_name", "Manufacturer"], ["category", "Category"],
    ["mrp", "MRP", "money"], ["retailer_price", "Price", "money"], ["inventory", "Stock"], ["status", "Status", "badge"]] },
  orders: { title: "Orders", url: "/admin/orders", cols: [
    ["order_number", "Order"], ["retailer_name", "Retailer"], ["manufacturer_name", "Manufacturer"],
    ["total", "Total", "money"], ["payment_status", "Payment", "badge"], ["status", "Status", "badge"]] },
  payments: { title: "Payments", url: "/admin/payments", cols: [
    ["session_id", "Session"], ["order_id", "Order"], ["amount", "Amount", "money"], ["payment_status", "Status", "badge"], ["created_at", "Date", "date"]] },
  ledger: { title: "Financial Ledger", url: "/admin/ledger", cols: [
    ["order_number", "Order"], ["type", "Entry"], ["kind", "Kind"], ["amount", "Amount", "money"], ["created_at", "Date", "date"]] },
  returns: { title: "Returns", url: "/admin/returns", cols: [
    ["order_number", "Order"], ["reason", "Reason"], ["quantity", "Qty"], ["status", "Status", "badge"], ["created_at", "Date", "date"]] },
  audit: { title: "Audit Logs", url: "/admin/audit-logs", cols: [
    ["action", "Action"], ["role", "Role"], ["entity", "Entity"], ["reason", "Reason"], ["timestamp", "Time", "date"]] },
  settlements: { title: "Settlements", url: "/admin/settlements", settlement: true, cols: [
    ["order_number", "Order"], ["manufacturer_name", "Manufacturer"], ["amount", "Payout", "money"], ["status", "Status", "badge"], ["bank_reference", "Ref"]] },
};

export default function Records() {
  const { section } = useParams();
  const cfg = CFG[section];
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ amount: "", bank_reference: "", provider: "Manual", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => { setRows(null); api.get(cfg.url).then((r) => setRows(r.data)).catch(() => setRows([])); };
  useEffect(() => { if (cfg) load(); }, [section]);

  if (!cfg) return <EmptyState title="Unknown section" icon={Database} />;
  if (!rows) return <Loader />;

  const cell = (row, [key, , type]) => {
    const v = row[key];
    if (type === "money") return <span className="num text-forest">{money(v)}</span>;
    if (type === "badge") return <StatusBadge status={v} />;
    if (type === "date") return <span className="num text-ink/60">{(v || "").slice(0, 10)}</span>;
    return <span className={key.includes("_id") || key.includes("number") || key === "session_id" ? "num text-ink/70" : "text-ink"}>{v ?? "—"}</span>;
  };

  async function record() {
    setBusy(true);
    try { await api.post(`/admin/settlements/${sel.id}/record`, { ...form, amount: +form.amount });
      toast.success("Settlement recorded"); setSel(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }

  async function verifyRetailer(id, action) {
    try { await api.post(`/admin/retailers/${id}/verify`, { action }); toast.success("Retailer updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  }

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl font-semibold text-forest">{cfg.title}</h1>
      {rows.length === 0 ? <EmptyState title={`No ${cfg.title.toLowerCase()} yet`} icon={Database} /> : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-psage/40 text-left"><tr className="overline text-forest">
              {cfg.cols.map((c) => <th key={c[0]} className="whitespace-nowrap px-4 py-3">{c[1]}</th>)}
              {(cfg.settlement || cfg.approve) && <th className="px-4 py-3"></th>}
            </tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id || row.session_id} data-testid={`rec-${row.id || row.session_id}`} className="border-t border-border hover:bg-psage/20">
                  {cfg.cols.map((c) => <td key={c[0]} className="whitespace-nowrap px-4 py-3">{cell(row, c)}</td>)}
                  {cfg.settlement && (
                    <td className="px-4 py-3">
                      {row.status !== "SETTLED" && <Button data-testid={`settle-${row.id}`} size="sm" className="bg-forest text-white hover:bg-forest/90"
                        onClick={() => { setSel(row); setForm({ amount: row.amount, bank_reference: "", provider: "Manual", notes: "" }); }}>Record</Button>}
                    </td>
                  )}
                  {cfg.approve && (
                    <td className="px-4 py-3">
                      {row.verification_status !== "VERIFIED" ? (
                        <Button data-testid={`approve-retailer-${row.id}`} size="sm" className="bg-forest text-white hover:bg-forest/90" onClick={() => verifyRetailer(row.id, "APPROVE")}>Approve</Button>
                      ) : (
                        <Button data-testid={`suspend-retailer-${row.id}`} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => verifyRetailer(row.id, "SUSPEND")}>Suspend</Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record settlement · {sel?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Amount (₹)</Label><Input data-testid="settle-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div><Label className="text-xs">Bank reference</Label><Input data-testid="settle-ref" value={form.bank_reference} onChange={(e) => setForm({ ...form, bank_reference: e.target.value })} /></div>
            <div><Label className="text-xs">Provider</Label><Input data-testid="settle-provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} /></div>
            <div><Label className="text-xs">Notes</Label><Input data-testid="settle-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="settle-save" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={record}>Mark Settled</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
